import { useCallback, useEffect, useRef, useState } from 'react';
// --- external imports ------

/**
 * Owns the `isProcessing` flag of an auth screen.
 *
 * Screens must never flip the flag by hand: it is cleared in a `finally`-style
 * path, so a resolved request can not leave a loading cursor spinning. Requests
 * that belong to one user action (Firebase + user system) go into a single
 * `runRequest` call, so the screen holds one loading state for the whole chain.
 *
 * `shouldKeepProcessingOnSuccess` is for flows that hand off to `UserProvider`
 * once the Firebase call resolves (login): the request succeeded, but the flow
 * has not finished, so the screen keeps its loading state until it navigates
 * away or `stopProcessing` is called.
 */
function useAuthRequest(options) {
	const [isProcessing, setIsProcessing] = useState(false);

	const isMountedRef = useRef(true);

	useEffect(function trackMountedState() {
		isMountedRef.current = true;

		return function markAsUnmounted() {
			isMountedRef.current = false;
		};
	}, []);

	const shouldKeepProcessingOnSuccess = options?.shouldKeepProcessingOnSuccess === true;

	const stopProcessing = useCallback(() => {
		if (isMountedRef.current) {
			setIsProcessing(false);
		}
	}, []);

	const runRequest = useCallback(async (request) => {
		setIsProcessing(true);

		try {
			const result = await request();

			if (!shouldKeepProcessingOnSuccess) {
				stopProcessing();
			}

			return result;
		}
		catch (error) {
			stopProcessing();
			throw error;
		}
	}, [shouldKeepProcessingOnSuccess, stopProcessing]);

	return { isProcessing, runRequest, stopProcessing };
}

export default useAuthRequest;
